import React, { useState } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "components/Toast";
import ReUseAbleTable, { Column } from "shared/ReUseAbleTable";
import DashboardTopTitle from "components/DashboardTopTitle";
import {
  createExpense,
  CreateExpensePayload,
  getExpenses,
  Expense,
  ExpensesResponse,
} from "services/expenses";
import { VisibilityOutlined, Close } from "@mui/icons-material";

const Expenses: React.FC = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);
  const [selectedReceiptUrl, setSelectedReceiptUrl] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState<{
    receipt: File | null;
    date: string;
    amount: string;
    description: string;
    category: string;
    paidBy: string;
    notes: string;
    recordedBy: string;
  }>({
    receipt: null,
    date: "",
    amount: "",
    description: "",
    category: "",
    paidBy: "",
    notes: "",
    recordedBy: "",
  });

  const { data: expensesData, isLoading: isExpensesLoading } =
    useQuery<ExpensesResponse>({
      queryKey: ["expenses"],
      queryFn: getExpenses,
    });

  const createExpenseMutation = useMutation({
    mutationFn: (payload: CreateExpensePayload) => createExpense(payload),
    onSuccess: async () => {
      showToast("Expense recorded successfully", "success");
      await queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setOpenAddDialog(false);
      setExpenseForm({
        receipt: null,
        date: "",
        amount: "",
        description: "",
        category: "",
        paidBy: "",
        notes: "",
        recordedBy: "",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to save expense record";
      showToast(message, "error");
    },
  });

  const handleViewReceipt = (receiptFilename: string, description: string) => {
    if (!receiptFilename) {
      showToast("No receipt available for this expense", "info");
      return;
    }

    try {
      const receiptUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}images/expensereceipts/${receiptFilename}`;
      setSelectedReceiptUrl(receiptUrl);
      setSelectedDescription(description);
      setOpenModal(true);
    } catch (error) {
      showToast("Failed to open receipt", "error");
      console.error("View error:", error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReceiptUrl("");
    setSelectedDescription("");
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    if (createExpenseMutation.isPending) return;
    setOpenAddDialog(false);
  };

  const handleChange = (key: keyof typeof expenseForm, value: any) => {
    setExpenseForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const amountNumber = Number(expenseForm.amount);
    if (
      !expenseForm.date ||
      !expenseForm.description.trim() ||
      !expenseForm.category.trim() ||
      !expenseForm.paidBy.trim() ||
      !expenseForm.recordedBy.trim() ||
      Number.isNaN(amountNumber) ||
      amountNumber <= 0
    ) {
      showToast("Please fill all required fields correctly", "error");
      return;
    }

    createExpenseMutation.mutate({
      receipt: expenseForm.receipt,
      date: expenseForm.date,
      amount: amountNumber,
      description: expenseForm.description.trim(),
      category: expenseForm.category.trim(),
      paidBy: expenseForm.paidBy.trim(),
      notes: expenseForm.notes.trim() || undefined,
      recordedBy: expenseForm.recordedBy.trim(),
    });
  };

  const columns: Column[] = [
    { id: "description", label: "Description", minWidth: 200 },
    { id: "category", label: "Category", minWidth: 120 },
    {
      id: "amount",
      label: "Amount",
      minWidth: 100,
      align: "right",
      format: (value: number) => `₦${value.toLocaleString()}`,
    },
    {
      id: "date",
      label: "Date",
      minWidth: 120,
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    { id: "paidBy", label: "Paid By", minWidth: 120 },
    { id: "recordedBy", label: "Recorded By", minWidth: 120 },
    {
      id: "createdAt",
      label: "Created",
      minWidth: 120,
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      id: "view",
      label: "Receipt",
      minWidth: 80,
      align: "center",
      format: (value: any) => (
        <IconButton
          size="small"
          onClick={() => handleViewReceipt(value.receipt, value.description)}
          disabled={!value.receipt}
          title={value.receipt ? "View receipt" : "No receipt available"}
        >
          <VisibilityOutlined
            fontSize="small"
            color={value.receipt ? "primary" : "disabled"}
          />
        </IconButton>
      ),
    },
  ];

  const expenses = expensesData?.data?.expenses || [];

  const rows = expenses.map((item: Expense) => ({
    id: item._id,
    description: item.description,
    category: item.category,
    amount: item.amount,
    date: item.date,
    paidBy: item.paidBy,
    recordedBy: item.recordedBy,
    createdAt: item.createdAt,
    view: {
      receipt: item.receipt,
      description: item.description,
    },
  }));

  if (isExpensesLoading) {
    return (
      <Box
        bgcolor="#FFFFFF"
        sx={{
          borderRadius: "10px",
          border: { xs: "transparent", sm: "1px solid #E4E7EC" },
          p: { xs: 0, sm: 3 },
        }}
      >
        <DashboardTopTitle
          title="Expenses"
          fontSize={20}
          subtitle="Loading expenses..."
        />
      </Box>
    );
  }

  return (
    <Box
      bgcolor="#FFFFFF"
      sx={{ borderRadius: "10px", border: "1px solid #E4E7EC" }}
      p="24px"
    >
      <DashboardTopTitle
        title="Expenses"
        fontSize={20}
        subtitle="Manage and track all expenses"
        secondBtn
        secondBtnTitle="Add New Record"
        onClickSecond={handleOpenAddDialog}
      />
      <ReUseAbleTable columns={columns} rows={rows} />

      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Add Expense Record
          </Typography>
          <IconButton onClick={handleCloseAddDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={expenseForm.date ? new Date(expenseForm.date) : null}
                onChange={(newValue) => {
                  handleChange(
                    "date",
                    newValue ? format(newValue, "yyyy-MM-dd") : "",
                  );
                }}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Amount"
              type="number"
              value={expenseForm.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={expenseForm.description}
              onChange={(e) => handleChange("description", e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Category"
              value={expenseForm.category}
              onChange={(e) => handleChange("category", e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Paid By"
              value={expenseForm.paidBy}
              onChange={(e) => handleChange("paidBy", e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Recorded By"
              value={expenseForm.recordedBy}
              onChange={(e) => handleChange("recordedBy", e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Notes"
              value={expenseForm.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: 12, color: "#667085" }}>
                {expenseForm.receipt
                  ? expenseForm.receipt.name
                  : "No file selected"}
              </Typography>
              <Button variant="outlined" component="label">
                Upload Receipt
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleChange("receipt", e.target.files?.[0] || null)
                  }
                />
              </Button>
            </Box>

            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: "#379E66", "&:hover": { bgcolor: "#2b7d4f" } }}
              disabled={createExpenseMutation.isPending}
            >
              {createExpenseMutation.isPending ? "Saving..." : "Save Expense"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", p: 1, height: "80vh" },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={selectedReceiptUrl}
            alt="Expense Receipt"
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            }}
            onError={(e: any) => {
              e.target.src =
                "https://via.placeholder.com/400?text=No+Receipt+Found";
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Expenses;
